import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const content = type === 'privacy' ? privacyContent : termsContent;
  const title = type === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-14">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-15">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0" data-id="element-16">
          <h2 className="font-serif text-2xl text-brand-dark" data-id="element-17">{title}</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-brand-dark hover:bg-gray-100 transition-colors" aria-label="Cerrar" data-id="element-18">
            <XIcon className="w-5 h-5" data-id="element-19" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-6 text-gray-700 text-sm leading-relaxed space-y-4" data-id="element-20">
          {content}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0" data-id="element-21">
          <button onClick={onClose} className="w-full py-3 bg-brand-dark text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors text-sm" data-id="element-22">
            Cerrar
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
const privacyContent = <>
    <p className="text-xs text-gray-400" data-id="element-23">Última actualización: marzo 2026</p>

    <h3 className="font-bold text-brand-dark text-base mt-2" data-id="element-24">
      1. Información que Recopilamos
    </h3>
    <p data-id="element-25">
      Cuando adquieres nuestra guía digital, recopilamos únicamente la
      información necesaria para procesar tu compra: nombre, dirección de correo
      electrónico y datos de pago. No almacenamos datos de tarjetas de crédito;
      estos son procesados de forma segura por nuestro proveedor de pagos.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-26">
      2. Uso de la Información
    </h3>
    <p data-id="element-27">Utilizamos tu información personal exclusivamente para:</p>
    <ul className="list-disc pl-5 space-y-1" data-id="element-28">
      <li data-id="element-29">Procesar y entregar tu compra.</li>
      <li data-id="element-30">Enviarte el enlace de descarga y actualizaciones del producto.</li>
      <li data-id="element-31">Responder a consultas de soporte relacionadas con tu compra.</li>
    </ul>
    <p data-id="element-32">
      No vendemos, alquilamos ni compartimos tu información personal con
      terceros con fines comerciales.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-33">
      3. Cookies y Tecnologías de Seguimiento
    </h3>
    <p data-id="element-34">
      Nuestro sitio web puede utilizar cookies esenciales para el funcionamiento
      del sitio y cookies analíticas para comprender cómo los visitantes
      interactúan con la página. Puedes desactivar las cookies en la
      configuración de tu navegador.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-35">
      4. Protección de Datos
    </h3>
    <p data-id="element-36">
      Implementamos medidas de seguridad técnicas y organizativas para proteger
      tu información personal contra acceso no autorizado, pérdida o alteración.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-37">5. Tus Derechos</h3>
    <p data-id="element-38">
      Tienes derecho a acceder, rectificar o eliminar tus datos personales en
      cualquier momento. Para ejercer estos derechos, puedes contactarnos a
      través de los canales indicados en tu correo de confirmación de compra.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-39">
      6. Cambios en esta Política
    </h3>
    <p data-id="element-40">
      Nos reservamos el derecho de actualizar esta política de privacidad en
      cualquier momento. Los cambios serán publicados en esta misma página con
      la fecha de actualización correspondiente.
    </p>
  </>;
const termsContent = <>
    <p className="text-xs text-gray-400" data-id="element-41">Última actualización: marzo 2026</p>

    <h3 className="font-bold text-brand-dark text-base mt-2" data-id="element-42">
      1. Aceptación de los Términos
    </h3>
    <p data-id="element-43">
      Al acceder a este sitio web y/o adquirir la guía digital "Cenas Sin
      Batallas", aceptas estos términos y condiciones en su totalidad. Si no
      estás de acuerdo con alguno de estos términos, te rogamos que no utilices
      este sitio ni adquieras el producto.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-44">2. Producto Digital</h3>
    <p data-id="element-45">
      "Cenas Sin Batallas" es una guía digital en formato PDF. Al completar la
      compra, recibirás acceso inmediato al contenido descargable. Debido a la
      naturaleza digital del producto, no se aceptan devoluciones una vez
      entregado el acceso.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-46">
      3. Uso del Contenido
    </h3>
    <p data-id="element-47">
      El contenido de la guía es para uso personal y familiar exclusivamente.
      Queda prohibido:
    </p>
    <ul className="list-disc pl-5 space-y-1" data-id="element-48">
      <li data-id="element-49">Reproducir, distribuir o compartir el contenido con terceros.</li>
      <li data-id="element-50">
        Utilizar el contenido con fines comerciales sin autorización expresa.
      </li>
      <li data-id="element-51">Modificar, adaptar o crear obras derivadas del contenido.</li>
    </ul>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-52">
      4. Exención de Responsabilidad
    </h3>
    <p data-id="element-53">
      La información proporcionada en esta guía tiene fines educativos e
      informativos. No sustituye el consejo médico, nutricional o psicológico
      profesional. Si tu hijo presenta trastornos alimentarios, sensoriales o
      médicos, consulta con un profesional de la salud antes de aplicar
      cualquier método.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-54">5. Resultados</h3>
    <p data-id="element-55">
      Los resultados mencionados en esta página son basados en experiencias
      reportadas y pueden variar según cada familia y situación individual. No
      garantizamos resultados específicos.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-56">
      6. Propiedad Intelectual
    </h3>
    <p data-id="element-57">
      Todo el contenido de este sitio web y de la guía digital, incluyendo
      textos, imágenes, diseño y marca, está protegido por derechos de autor y
      es propiedad exclusiva de Cenas Sin Batallas.
    </p>

    <h3 className="font-bold text-brand-dark text-base" data-id="element-58">7. Modificaciones</h3>
    <p data-id="element-59">
      Nos reservamos el derecho de modificar estos términos en cualquier
      momento. Las modificaciones entrarán en vigor desde su publicación en esta
      página.
    </p>
  </>;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brand-dark text-gray-400 py-10 text-center text-sm" data-id="element-60">
        <div className="max-w-4xl mx-auto px-4" data-id="element-61">
          <div className="flex justify-center gap-6 mb-6" data-id="element-62">
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors underline underline-offset-4 decoration-gray-600 hover:decoration-white" data-id="element-63">
              Política de Privacidad
            </button>
            <span className="text-gray-600" data-id="element-64">|</span>
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors underline underline-offset-4 decoration-gray-600 hover:decoration-white" data-id="element-65">
              Términos y Condiciones
            </button>
          </div>

          <p data-id="element-66">
            © {new Date().getFullYear()} Cenas Sin Batallas. Todos los derechos
            reservados.
          </p>
          <p className="mt-2 text-xs opacity-60" data-id="element-67">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca comercial de FACEBOOK, Inc.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-68">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-69" />}
      </AnimatePresence>
    </>;
}